import React, { useEffect, useContext, useRef, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useHistory } from 'react-router-dom'
import clsx from 'clsx'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import MainTemplate from "../templates/MainTemplate"
import {
  Avatar,
  Paper,
  Table,
  Link,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TableHead,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import GroupIcon from '@material-ui/icons/Group'
import { GlobalContext } from '../../hooks/reducer'
import { actions } from "../../hooks/useLoginUserReducer"
import { actions as adminActions } from "../../hooks/useAdminReducer"
import CustomModal from "../common/CustomModal"

// 時間の設定
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')

const useStyles = makeStyles((theme) => ({
  root: { width: '100%' },
  paper: { width: '100%', border: '1px solid rgba(224,224,224,1)', marginBottom: theme.spacing(2) },
  table: { minWidth: 750 },
  tag: { marginRight: 10 },
  fixedLeft: { position: 'sticky', left: 0, background: '#fff', zIndex: 2 },
  fixedName: { position: 'sticky', left: 60, background: '#fff', zIndex: 1 },
  scrollbar: { width: '100%', height: 10, overflowX: 'auto', overflowY: 'hidden', '&::-webkit-scrollbar': { height: 10 }, '&::-webkit-scrollbar-track': { borderRadius: 5 }, '&::-webkit-scrollbar-thumb': { borderRadius: 5, backgroundColor: '#e7e7e7' } },
  scrollbarInner: { width: 2000, height: 1 },
  scrollbox: { width: '100%', marginTop: 5, overflowX: 'auto', overflowY: 'hidden', '&::-webkit-scrollbar': { height: 10 }, '&::-webkit-scrollbar-track': { borderRadius: 5 }, '&::-webkit-scrollbar-thumb': { borderRadius: 5, backgroundColor: '#e7e7e7' } },
  scrollboxInner: { width: 2000, marginBottom: 5, backgroundColor: '#F9F8F6' },
  avatar: { transition: 'border 0.3s ease', cursor: 'pointer', border: '2px solid transparent', '&:hover': { border: '2px solid rgba(57,84,117,0.7)', opacity: 0.8 } },
  caption: { margin: '10px 0' },
  controls: { display: 'flex', alignItems: 'center', marginBottom: theme.spacing(2), '& > *:not(:last-child)': { marginRight: theme.spacing(2) } },
}))

const EnhancedTableHead = ({ classes }) => {
  const headCells = [
    { id: 'members', label: 'メンバー', className: classes.fixedLeft, style: { width: 60 } },
    { id: 'name', label: '会社名', className: classes.fixedName, style: { minWidth: 200 } },
    { id: 'id', label: '会社ID', style: { minWidth: 250 } },
    { id: 'imagePath', label: 'アイコン画像パス' },
    { id: 'industryTags', label: '業種・業界', style: { minWidth: 200 } },
    { id: 'prefTags', label: '所在地', style: { minWidth: 200 } },
    { id: 'memberNumTags', label: '社員数', style: { minWidth: 200 } },
    { id: 'description', label: '説明', style: { minWidth: 250 } },
    { id: 'createAt', label: '登録日' },
  ]

  return (
    <TableHead>
      <TableRow>
        {headCells.map((cell) => (
          <TableCell
            key={cell.id}
            style={cell.style}
            className={cell.className}
          >
            {cell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

const Companies = React.memo(() => {
  const classes = useStyles()
  const history = useHistory()
  const scrollBarRef = useRef(null)
  const scrollBoxRef = useRef(null)
  const { state: { loginUser, admin }, dispatch } = useContext(GlobalContext)
  const { companies } = admin

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [filterName, setFilterName] = useState('')

  const filteredCompanies = useMemo(
    () => companies.filter((c) => c.name.toLowerCase().includes(filterName.toLowerCase())),
    [companies, filterName]
  )

  // ログインチェック & 企業データ取得
  useEffect(() => {
    if (!loginUser.isLogin) {
      actions.loginCheck(dispatch, history)
    } else if (companies.length === 0) {
      adminActions.getCompanies(dispatch)
    }
    return () => dispatch({ type: 'COMMON_RESET' })
  }, [loginUser.isLogin, companies.length, dispatch, history])

  // スクロール同期
  useEffect(() => {
    const bar = scrollBarRef.current
    const box = scrollBoxRef.current
    if (!bar || !box) return

    const syncBar = () => { box.scrollLeft = bar.scrollLeft }
    const syncBox = () => { bar.scrollLeft = box.scrollLeft }

    bar.addEventListener('scroll', syncBar)
    box.addEventListener('scroll', syncBox)
    return () => {
      bar.removeEventListener('scroll', syncBar)
      box.removeEventListener('scroll', syncBox)
    }
  }, [])

  return (
    <MainTemplate>
      <CustomModal />

      <div className={classes.root}>
        <div className={classes.controls}>
          <TextField
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            label="会社名で検索"
            variant="outlined"
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setFilterName('')} edge="end">
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>

        <Typography variant="body2" className={classes.caption}>
          ※アイコンをクリックすると、会社ごとの事例共有一覧を見れます。
        </Typography>

        <Paper className={classes.paper}>
          <div ref={scrollBarRef} className={classes.scrollbar}>
            <div className={classes.scrollbarInner} />
          </div>

          <TableContainer ref={scrollBoxRef} className={classes.scrollbox}>
            <Table className={clsx(classes.table, classes.scrollboxInner)} size="medium">
              <EnhancedTableHead classes={classes} />
              <TableBody>
                {filteredCompanies
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow hover key={row.companyId}>
                      {/* メンバー一覧ボタン */}
                      <TableCell className={classes.fixedLeft}>
                        <IconButton
                          size="small"
                          onClick={() => history.push(`/admin/company/${row.companyId}/members`)}
                        >
                          <GroupIcon />
                        </IconButton>
                      </TableCell>

                      {/* 会社名 */}
                      <TableCell className={classes.fixedName}>
                        <Grid container alignItems="center">
                          <Avatar
                            src={row.imageUrl}
                            className={classes.avatar}
                            style={{ marginRight: 10 }}
                            onClick={() => history.push(`/company/${row.companyId}`)}
                          />
                          {row.url ? (
                            <Link href={row.url} target="_blank" underline="hover" color="primary">
                              {row.name}
                            </Link>
                          ) : (
                            <Typography component="p">{row.name}</Typography>
                          )}
                        </Grid>
                      </TableCell>

                      <TableCell>{row.companyId}</TableCell>
                      <TableCell>
                        {row.imageUrl && (
                          <Link href={row.imageUrl} target="_blank" underline="hover" color="primary">
                            {`company/${row.imageName}`}
                          </Link>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.industryTags.map((tag) => (
                          <span key={tag.value} className={classes.tag}>
                            {tag.label},
                          </span>
                        ))}
                      </TableCell>
                      <TableCell>
                        {row.prefTags.map((tag) => (
                          <span key={tag.value} className={classes.tag}>
                            {tag.label},
                          </span>
                        ))}
                      </TableCell>
                      <TableCell>
                        <span className={classes.tag}>{row.memberNumTags.label}</span>
                      </TableCell>
                      <TableCell className="indention">{row.description}</TableCell>
                      <TableCell>{dayjs(row.createAt).format('YYYY/MM/DD')}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[25, 50]}
            component="div"
            count={companies.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
          />
        </Paper>
      </div>
    </MainTemplate>
  )
})

export default Companies
